import { assertNever } from "@suse-o11y/assert-never";
import React from "react";

import { PortalToBody } from "../react/portal/Portal";

export type TimeLinePopoverPlacement = "bottom-left" | "bottom-right";

export interface Point {
	x: number;
	y: number;
}

interface PositionedPopoverProps {
	position: Point;

	/**
	 * @default 0
	 */
	offset?: number | Point;

	/**
	 * @default "bottom-right"
	 */
	placement?: TimeLinePopoverPlacement;

	/**
	 * The width of the Popover in pixels.
	 * @default 340
	 */
	width?: number;

	onHover?: (isHovered: boolean) => void;
}

export const PositionedPopover: React.FC<React.PropsWithChildren<PositionedPopoverProps>> = ({
	position,
	placement = "bottom-right",
	offset = 0,
	width = 340,
	onHover,
	children,
}) => (
	<PortalToBody>
		<div
			className="fixed z-50 rounded-sm border border-grey-300 bg-white p-4 shadow-tooltip"
			onMouseEnter={() => onHover?.(true)}
			onMouseLeave={() => onHover?.(false)}
			style={{
				width,
				...placementToStyles({
					placement,
					host: document.body,
					position,
					offset: typeof offset === "number" ? { x: offset, y: offset } : offset,
					popoverWidth: width,
				}),
			}}
		>
			{children}
		</div>
	</PortalToBody>
);

const placementToStyles = ({
	placement,
	host,
	position,
	offset,
	popoverWidth,
}: {
	placement: TimeLinePopoverPlacement;
	host: HTMLElement;
	position: Point;
	offset: Point;
	popoverWidth: number;
}): React.CSSProperties => {
	const isEnoughSpaceOnRight = position.x + popoverWidth + offset.x < host.clientWidth;
	const isEnoughSpaceOnLeft = position.x - popoverWidth - offset.x > 0;

	// Adjust placement if there's not enough space
	const adjustedPlacement =
		(placement === "bottom-right" && !isEnoughSpaceOnRight && "bottom-left") ||
		(placement === "bottom-left" && !isEnoughSpaceOnLeft && "bottom-right") ||
		placement;

	switch (adjustedPlacement) {
		case "bottom-left": {
			return {
				right: host.clientWidth - position.x + offset.x,
				top: position.y + offset.y,
			};
		}

		case "bottom-right": {
			return {
				left: position.x + offset.x,
				top: position.y + offset.y,
			};
		}

		default: {
			return assertNever(adjustedPlacement);
		}
	}
};
